import { Star } from 'lucide-react'
import { PlayBackButton } from '@/components/play/PlayBackButton'
import { ClockIcon } from '@/components/p2p/icons'
import { useCountdown } from '@/hooks/use-countdown'
import { formatP2pVolume } from '@/lib/p2p'
import { cn } from '@/lib/utils'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { selectIsP2pFavorite, toggleP2pFavorite } from '@/store/slices/p2pSlice'
import type { P2PMarket } from '@/types/p2p'

interface P2pMarketHeaderProps {
	market: P2PMarket
}

export function P2pMarketHeader({ market }: P2pMarketHeaderProps) {
	const dispatch = useAppDispatch()
	const isFavorite = useAppSelector(selectIsP2pFavorite(market.id))
	const timeLeft = useCountdown(market.expire_at, 'full')

	return (
		<div className='flex w-full items-start gap-3'>
			<PlayBackButton />

			<div className='min-w-0 flex-1'>
				<h1 className='text-2xl font-semibold tracking-[-0.36px] text-foreground sm:text-[28px]'>{market.title}</h1>
				<div className='mt-2 flex flex-wrap items-center gap-4 text-sm font-medium tracking-[-0.21px] text-muted-foreground'>
					<span>{formatP2pVolume(market.total_volume)}</span>
					<span className='flex items-center gap-1.5'>
						<ClockIcon className='h-4 w-4 text-subtle-foreground' />
						{timeLeft}
					</span>
				</div>
			</div>

			<button
				type='button'
				onClick={() => dispatch(toggleP2pFavorite(market.id))}
				className={cn(
					'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-border bg-secondary text-foreground transition-colors hover:bg-accent',
					isFavorite && 'border-primary/40 bg-primary/10 text-primary',
				)}
				aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
				aria-pressed={isFavorite}
			>
				<Star className={cn('h-4 w-4', isFavorite && 'fill-current')} />
			</button>
		</div>
	)
}
